import clsx from 'clsx';
import { highlight, languages } from 'prismjs';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import Editor from 'react-simple-code-editor';
import 'prismjs/components/prism-clike';
import 'prismjs/components/prism-javascript';
import 'prismjs/themes/prism.css'; // Example style, you can use another

import { useAgentEdit } from '@/app/agents/edit/state/agentEditContext';
import { getNodeTypeLabel } from '@/app/agents/edit/util/nodeTypeUtils';
import { Button } from '@/components/button/button';
import { InputWithTemplateVars, TemplateVarItem } from '@/components/inputWithTemplateVars/inputWithTemplateVars';
import Modal from '@/components/modal/modal';
import { Heading } from '@/components/typography/heading';
import { Text } from '@/components/typography/text';
import {
  AgentConfigStepAction,
  AgentConfigStepInputs,
  AgentConfigStepLLMAction,
  AgentConfigStepLLMActionMCPClient,
  AgentConfigStepLLMActionTool,
  AgentConfigStepTypeEnum
} from '@/types/agent';


type Props = {
  llmAction: AgentConfigStepLLMAction;
  editable: boolean;
  inputs: AgentConfigStepInputs;
  models: string[];
  onActionUpdate: <T extends AgentConfigStepAction>( action: T ) => void;
  stepId: string;
};

export default function ActionLLM( props: Props ) {
  const {
    llmAction,
    editable,
    inputs,
    models,
    onActionUpdate,
    stepId
  } = props;

  const {
    conversation_key,
    model,
    prompt,
    temperature = 0.5,
    tools,
    mcp_servers
  } = llmAction;

  // Get global context and validation errors from React Context
  const { config, validationErrors } = useAgentEdit();
  const globalContext = config?.context || {};
  const modelError = validationErrors[stepId]?.action?.model;
  const promptError = validationErrors[stepId]?.action?.prompt;

  const [ conversationKeyValue, setConversationKey ] =
    useState<string>( conversation_key );
  const [ modelValue, setModel ] = useState<string>( model );
  const [ promptValue, setPrompt ] = useState<string>( prompt );
  const [ editToolShow, setEditToolShow ] = useState<boolean>( false );
  const [ editToolIndex, setEditToolIndex ] = useState<number | undefined>();
  const [ editToolCode, setEditToolCode ] = useState<string>( '' );
  const [ toolsValue, setTools ] = useState<AgentConfigStepLLMActionTool[]>(
    tools || []
  );
  const [ mcpValue, setMCP ] = useState<AgentConfigStepLLMActionMCPClient[]>(
    mcp_servers || []
  );
  const [ temperatureValue, setTemperature ] = useState<number>( temperature );

  useEffect( () => {
    setConversationKey( conversation_key );
    setPrompt( prompt );
    setModel( model );
    setTemperature( temperature );
    setTools( tools || [] );
  }, [ conversation_key, editable, model, prompt, temperature, tools ] );

  const makeAction = (): AgentConfigStepLLMAction => {
    return {
      conversation_key: conversationKeyValue,
      model: modelValue,
      prompt: promptValue,
      temperature: temperatureValue,
      tools: toolsValue,
      mcp_servers: mcpValue
    };
  };

  const onConvoKeyChange = ( event: React.ChangeEvent<HTMLSelectElement> ) => {
    const nextConvoKeyValue = event.currentTarget.value;
    setConversationKey( nextConvoKeyValue );

    const res = {
      ...makeAction(),
      conversation_key: nextConvoKeyValue
    };
    onActionUpdate( res );
  };

  const onPromptChange = ( event: React.ChangeEvent<HTMLTextAreaElement> ) => {
    const nextPrompt = event.currentTarget.value;
    setPrompt( nextPrompt );

    const res = {
      ...makeAction(),
      prompt: nextPrompt
    };
    onActionUpdate( res );
  };

  const onModelChange = ( event: React.ChangeEvent<HTMLSelectElement> ) => {
    const nextModel = event.currentTarget.value;
    setModel( nextModel );

    const res = {
      ...makeAction(),
      model: nextModel
    };
    onActionUpdate( res );
  };

  const onTemperatureChange = ( event: React.ChangeEvent<HTMLInputElement> ) => {
    const nextModel = Number( event.currentTarget.value );
    setTemperature( nextModel );
    const res = {
      ...makeAction(),
      temperature: nextModel
    };
    onActionUpdate( res );
  };

  const onEditClick = ( index: number ) => {
    setEditToolIndex( index );
    setToolCode( JSON.stringify( toolsValue[index], undefined, 2 ) );
    setEditToolShow( true );
  };

  const onClose = () => setEditToolShow( false );

  const setToolCode = ( code: string ) => setEditToolCode( code );

  const onSave = async() => {
    if ( editToolIndex === undefined ) {
      throw Error( 'edit window showing but edit index not set' );
    }

    const editedToolCode = JSON.parse( editToolCode );
    const nextToolsValue = [ ...toolsValue ];
    nextToolsValue[editToolIndex] = editedToolCode;
    setTools( nextToolsValue );

    setEditToolShow( false );
    setEditToolCode( '' );

    const res = {
      ...makeAction(),
      tools: nextToolsValue
    };
    onActionUpdate( res );
  };

  const onAddMCP = () => {
    const defaultMCPClient: AgentConfigStepLLMActionMCPClient = {
      name: 'name',
      url: 'http://remote-url',
      token: 'sk-???'
    };
    const nextMCPValue: AgentConfigStepLLMActionMCPClient[] = [
      ...mcpValue,
      defaultMCPClient
    ];
    setMCP( nextMCPValue );

    const res = {
      ...makeAction(),
      mcp_servers: nextMCPValue
    };
    onActionUpdate( res );
  };

  const onAddTool = () => {
    const defaultTool: AgentConfigStepLLMActionTool = {
      type: 'function',
      function: {
        description: 'Generic Tool Example',
        name: `generic_tool-${ toolsValue.length }`,
        parameters: {
          type: 'object',
          properties: {
            location: {
              type: 'string',
              description: 'A parameter'
            }
          }
        }
      }
    };

    const nextToolsValue: AgentConfigStepLLMActionTool[] = [
      ...toolsValue,
      defaultTool
    ];
    setTools( nextToolsValue );

    const res = {
      ...makeAction(),
      tools: nextToolsValue
    };
    onActionUpdate( res );
  };

  const onRemoveClick = ( index: number ) => {
    const nextToolsValue = [ ...toolsValue ];
    nextToolsValue.splice( index, 1 );
    setTools( nextToolsValue );

    const res = {
      ...makeAction(),
      tools: nextToolsValue
    };
    onActionUpdate( res );
  };

  const onRemoveMCP = ( index: number ) => {
    const newMCP = [ ...mcpValue ];
    newMCP.splice( index, 1 );
    setMCP( newMCP );

    const res = {
      ...makeAction(),
      mcp_servers: newMCP
    };
    onActionUpdate( res );
  };

  const onMCPNameChange = ( value: string, index: number ) => {
    const newMCP = [ ...mcpValue ];
    newMCP[index].name = value;
    setMCP( newMCP );
    const res = {
      ...makeAction(),
      mcp_servers: newMCP
    };
    onActionUpdate( res );
  };
  const onMCPURLChange = ( value: string, index: number ) => {
    const newMCP = [ ...mcpValue ];
    newMCP[index].url = value;
    setMCP( newMCP );
    const res = {
      ...makeAction(),
      mcp_servers: newMCP
    };
    onActionUpdate( res );
  };
  const onMCPTokenChange = ( value: string, index: number ) => {
    const newMCP = [ ...mcpValue ];
    newMCP[index].token = value;
    setMCP( newMCP );
    const res = {
      ...makeAction(),
      mcp_servers: newMCP
    };
    onActionUpdate( res );
  };


  const renderEditTool = (): ReactElement => {
    return (
      <Modal>
        <form onSubmit={ onSave }>
          <Heading as='h2' size='md'>Edit tool</Heading>
          <div className={ clsx( 'layout__col', 'pad' ) }>
            <Editor
              disabled={ !editable }
              value={ editToolCode }
              onValueChange={ ( code ) => setToolCode( code ) }
              highlight={ ( code ) => highlight( code, languages.js, 'js' ) }
              padding={ 10 }
              style={ {
                fontFamily: '"Fira code", "Fira Mono", monospace',
                fontSize: 12
              } }
            />
          </div>
          <div className={ clsx( 'layout__row', 'pad', 'flex_right' ) }>
            {editable && (
              <Button variant='primary' size='sm' type='submit'>
                Save
              </Button>
            )}
            <Button variant='secondary' size='sm' onClick={ onClose }>
              Close
            </Button>
          </div>
        </form>
      </Modal>
    );
  };

  // Include both inputs and global context (with global. prefix) in prompt mapping options
  const inputMappingItems = Object.keys( inputs ).map(
    ( keyValue ): TemplateVarItem => {
      return { name: keyValue, id: keyValue, type: 'Inputs' };
    }
  );
  const globalContextMappingItems = Object.keys( globalContext ).map(
    ( keyValue ): TemplateVarItem => {
      return { name: `global.${ keyValue }`, id: `global.${ keyValue }`, type: 'Global Context' };
    }
  );
  const templateVars = [ ...inputMappingItems, ...globalContextMappingItems ];

  return (
    <>
      { editToolShow && renderEditTool() }

      <div className='configPane'>
        <Text as='div' className='paneTitle' size='md' weight='bold'>{ getNodeTypeLabel( AgentConfigStepTypeEnum.LLM ) } Action</Text>
        <Text as='label' size='sm' htmlFor='conversationalKey'>conversational key</Text>
        <select
          data-testid='config-edit-panel-action-llm-conversation-key-select'
          disabled={ !editable }
          id='conversationalKey'
          onChange={ onConvoKeyChange }
          value={ conversation_key }
        >
          {
            Object.keys( inputs ).map(
              ( keyValue, index ) => (
                <option key={ index } value={ keyValue }>
                  {keyValue}
                </option>
              )
            )
          }
        </select>
        <Text as='label' size='sm' htmlFor='model'>model</Text>
        <div
          className={ clsx( 'modelName', { error: !!modelError } ) }
          data-error-message={ modelError }
        >
          <select
            className={ clsx( { required: !!modelError } ) }
            data-testid='config-edit-panel-action-llm-model-select'
            disabled={ !editable }
            id='model'
            onChange={ onModelChange }
            value={ model }
          >
            <option key='placeholder' value={ '' }>--</option>
            {
              // Show current model first if it's not in the available models list
              model && !models.includes( model ) && (
                <option key='unavailable-model' value={ model }>
                  ⚠️ { model }
                </option>
              )
            }
            {
              models.map(
                ( keyValue, index ) => (
                  <option key={ index } value={ keyValue }>
                    { keyValue }
                  </option>
                )
              )
            }
          </select>
        </div>

        <Text as='label' size='sm' htmlFor='prompt'>prompt</Text>
        <InputWithTemplateVars
          data-testid='config-edit-panel-action-llm-prompt-textarea'
          disabled={ !editable }
          error={ promptError }
          id='prompt'
          inputType='textarea'
          onChange={ ( value ) => {
            setPrompt( value );
            const res = {
              ...makeAction(),
              prompt: value
            };
            onActionUpdate( res );
          } }
          value={ promptValue }
          variables={ templateVars }
        />
        <Text as='label' size='sm' htmlFor='temp'>Temperature: {temperatureValue}</Text>
        <input
          data-testid='config-edit-panel-action-llm-temperature-input'
          type='range'
          disabled={ !editable }
          id='temp'
          name='temp'
          min='0'
          max='1'
          step='0.1'
          onChange={ onTemperatureChange }
          value={ temperatureValue }
        />

        <Text as='label' size='sm'>Tools</Text>
        <br />
        {
          toolsValue.map(
            ( tool: AgentConfigStepLLMActionTool, index: number ) => {
              return (
                <div className='subitem' key={ index }>
                  { tool.function.name }
                  <br />
                  { tool.function.description }
                  <br />
                  {
                    editable ? (
                      <>
                        <button data-testid={ `config-edit-panel-action-llm-tool-${ index }-edit-button` } onClick={ () => onEditClick( index ) }>edit</button>
                        <button data-testid={ `config-edit-panel-action-llm-tool-${ index }-delete-button` } onClick={ () => onRemoveClick( index ) }>delete</button>
                      </>
                    ) : (
                      <button data-testid={ `config-edit-panel-action-llm-tool-${ index }-show-button` } onClick={ () => onEditClick( index ) }>show</button>
                    )
                  }
                </div>
              );
            }
          )
        }
        {
          editable && (
            <button data-testid='config-edit-panel-action-llm-add-tool-button' onClick={ onAddTool }>Add Tool</button>
          )
        }
        <br />
        <Text as='label' size='sm'>MCP Servers</Text>
        <br />
        {
          mcpValue.map(
            ( client: AgentConfigStepLLMActionMCPClient, index: number ) => {
              return (
                <div className='subitem' key={ index }>
                  name:
                  <input
                    data-testid={ `config-edit-panel-action-llm-mcp-${ index }-name-input` }
                    value={ client.name }
                    disabled={ !editable }
                    onChange={ ( e ) => onMCPNameChange( e.target.value, index ) }
                  />
                  url:
                  <input
                    data-testid={ `config-edit-panel-action-llm-mcp-${ index }-url-input` }
                    value={ client.url }
                    disabled={ !editable }
                    onChange={ ( e ) => onMCPURLChange( e.target.value, index ) }
                  />
                  token:
                  <input
                    data-testid={ `config-edit-panel-action-llm-mcp-${ index }-token-input` }
                    type='password'
                    value={ client.token }
                    disabled={ !editable }
                    onChange={ ( e ) => onMCPTokenChange( e.target.value, index ) }
                  />
                  <button data-testid={ `config-edit-panel-action-llm-mcp-${ index }-delete-button` } onClick={ () => onRemoveMCP( index ) }>delete</button>
                </div>
              );
            }
          )
        }
        {
          editable && (
            <button data-testid='config-edit-panel-action-llm-add-mcp-button' onClick={ onAddMCP }>Add MCP</button>
          )
        }
      </div>
    </>
  );
}
